// feat_407 — SOPORTE-F4a: Bandeja de Atención del staff (/superadmin/atencion).
// Consultas de los ADMIN de las empresas (no de sus clientes). Cross-company a propósito:
// master ve todo; un superadmin con marca y el permiso "Atiende soporte" ve SOLO las
// empresas de su marca (lo filtra el backend). Pantalla solo-superadmin → español hardcodeado.
//
// Tiempo real: room `support-staff` (master) o `support-staff-wl-<marca>` (reseller); el
// backend decide cuál al recibir `joinSupportStaff`. El evento `support` es una señal
// liviana: se refetchea por HTTP (con debounce).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper, Tab, Tabs, TextField, Typography, makeStyles,
} from "@material-ui/core";
import { toast } from "react-toastify";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import RequestCard from "./RequestCard";
import RequestDetail from "./RequestDetail";
import KnowledgeTab from "./KnowledgeTab";
import ConfigTab from "./ConfigTab";
import CoverageTab from "./CoverageTab";
import { AUTHORSHIP_LABELS, STATUS_LABELS, TYPE_LABELS, errorText } from "./shared";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(1) },
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
  },
  summary: { padding: theme.spacing(1, 1.5), marginBottom: theme.spacing(1.5), display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" },
  filters: { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", margin: theme.spacing(1, 0, 1.5) },
  selects: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: theme.spacing(1.5) },
  select: { minWidth: 160, flex: "1 1 160px", maxWidth: 260 },
}));

const VIEWS = ["bandeja", "ia", "banco", "cobertura", "config"];
const PERIODS = [["hoy", "Hoy"], ["7d", "7 días"], ["30d", "30 días"], ["", "Todo"]];
const TOGGLES = [
  ["withClarification", "Con repregunta"],
  ["lowConfidence", "Confianza < 0,85"],
  ["onlyManual", "Sólo manual"],
  ["unreviewed", "Sin revisar"],
  ["noManual", "Sin manual"],
];

const SuperAdminAtencion = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const view = VIEWS.includes(params.get("vista")) ? params.get("vista") : "bandeja";
  const openParam = Number(params.get("consulta")) || null;

  const [me, setMe] = useState(null);
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState("");
  const [toggles, setToggles] = useState({});
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [authorship, setAuthorship] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [companies, setCompanies] = useState([]);
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [refreshTick, setRefreshTick] = useState(0);

  const setQuery = useCallback((changes) => {
    const p = new URLSearchParams(history.location.search);
    Object.entries(changes).forEach(([k, v]) => (v == null || v === "" ? p.delete(k) : p.set(k, v)));
    history.replace({ pathname: history.location.pathname, search: p.toString() });
  }, [history]);

  useEffect(() => {
    api.get("/superadmin/support/me").then(({ data }) => setMe(data)).catch((err) => toast.error(errorText(err)));
    api.get("/superadmin/support/companies").then(({ data }) => setCompanies(data.companies || [])).catch(() => undefined);
  }, []);

  const filterKey = JSON.stringify({ view, period, toggles, status, type, authorship, companyId, q: debouncedSearch });
  const filters = useMemo(() => JSON.parse(filterKey), [filterKey]);

  const loadSummary = useCallback(() => {
    api.get("/superadmin/support/summary").then(({ data }) => setSummary(data)).catch(() => undefined);
  }, []);

  const loadList = useCallback(async (p = 1) => {
    if (filters.view !== "bandeja" && filters.view !== "ia") return;
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/support/requests", {
        params: {
          tab: filters.view,
          period: filters.period || undefined,
          status: filters.status || undefined,
          type: filters.type || undefined,
          authorship: filters.authorship || undefined,
          companyId: filters.companyId || undefined,
          search: filters.q || undefined,
          page: p,
          ...Object.fromEntries(Object.entries(filters.toggles).filter(([, v]) => v).map(([k]) => [k, "true"])),
        },
      });
      setList((prev) => (p > 1 && prev ? { ...data, requests: [...prev.requests, ...data.requests] } : data));
      setPage(p);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadList(1); }, [loadList, refreshTick]);
  useEffect(() => { loadSummary(); }, [loadSummary, refreshTick]);

  // Socket: señal liviana → refetch con debounce (una tanda de eventos = un solo GET).
  const timer = useRef(null);
  useEffect(() => {
    const socket = getSocket();
    const join = () => socket.emit("joinSupportStaff");
    join();
    const onSupport = () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setRefreshTick((t) => t + 1), 800);
    };
    socket.on("connect", join);
    socket.on("support", onSupport);
    return () => {
      socket.off("connect", join);
      socket.off("support", onSupport);
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  const s = summary || {};
  const master = !!me?.master;

  return (
    <div className={classes.root}>
      <Box style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
        <Typography variant="h5" component="h1">Atención</Typography>
        <Typography variant="body2" color="textSecondary">
          Consultas de los admins de las empresas{me && !master ? " · solo las empresas de tu marca" : ""}
        </Typography>
      </Box>

      <Paper variant="outlined" className={classes.summary} data-support-summary>
        <Typography variant="body2"><b>Hoy:</b></Typography>
        <Typography variant="body2">{s.consultas ?? "–"} consultas</Typography>·
        <Typography variant="body2">{s.respondioIa ?? "–"} respondió la IA</Typography>·
        <Typography variant="body2">{s.borrador ?? "–"} borrador</Typography>·
        <Typography variant="body2">{s.noSupo ?? "–"} no supo</Typography>·
        <Typography variant="body2">{s.repregunta ?? "–"} repregunta</Typography>·
        <Typography variant="body2">{s.sinRevisar ?? "–"} sin revisar</Typography>
        {s.pendientes > 0 && <Chip size="small" label={`${s.pendientes} esperan al equipo`} style={{ background: "#d32f2f", color: "#fff", marginLeft: "auto" }} />}
        {s.sinManual > 0 && <Chip size="small" label={`${s.sinManual} sin manual`} style={{ background: "#ff6f00", color: "#fff" }} />}
      </Paper>

      <Tabs
        value={view}
        onChange={(_e, v) => setQuery({ vista: v === "bandeja" ? null : v })}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="bandeja" label="Bandeja" />
        <Tab value="ia" label={`Respondidas por la IA${s.sinRevisar ? ` (${s.sinRevisar})` : ""}`} />
        <Tab value="banco" label="Banco de conocimiento" />
        <Tab value="cobertura" label={`Cobertura${s.sinManual ? ` (${s.sinManual} sin manual)` : ""}`} data-tab-cobertura />
        {master && <Tab value="config" label="Configuración" />}
      </Tabs>

      <Box style={{ marginTop: 12 }}>
        {(view === "bandeja" || view === "ia") && (
          <>
            <div className={classes.filters}>
              {PERIODS.map(([v, l]) => (
                <Chip key={v || "all"} label={l} clickable color={period === v ? "primary" : "default"} variant={period === v ? "default" : "outlined"} onClick={() => setPeriod(v)} />
              ))}
              <span style={{ width: 8 }} />
              {TOGGLES.map(([k, l]) => (
                <Chip key={k} label={l} clickable color={toggles[k] ? "primary" : "default"} variant={toggles[k] ? "default" : "outlined"} onClick={() => setToggles((t) => ({ ...t, [k]: !t[k] }))} data-filter={k} />
              ))}
            </div>
            <div className={classes.selects}>
              <TextField select size="small" variant="outlined" label="Empresa" className={classes.select} value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <MenuItem value="">Todas</MenuItem>
                {companies.map((c) => <MenuItem key={c.id} value={String(c.id)}>#{c.id} {c.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" variant="outlined" label="Estado" className={classes.select} value={status} onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="">{view === "bandeja" ? "Abiertas" : "Cualquiera"}</MenuItem>
                <MenuItem value="todas">Todas (incluye cerradas)</MenuItem>
                {Object.entries(STATUS_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
              <TextField select size="small" variant="outlined" label="Tipo" className={classes.select} value={type} onChange={(e) => setType(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {Object.entries(TYPE_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
              <TextField select size="small" variant="outlined" label="Quién respondió" className={classes.select} value={authorship} onChange={(e) => setAuthorship(e.target.value)}>
                <MenuItem value="">Cualquiera</MenuItem>
                {Object.entries(AUTHORSHIP_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
              </TextField>
              <TextField size="small" variant="outlined" label="Buscar texto o #ID" className={classes.select} value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            {!list ? (
              <CircularProgress />
            ) : list.requests.length === 0 ? (
              <Typography color="textSecondary" data-support-empty>
                {view === "ia" ? "No hay respuestas automáticas para este filtro." : "No hay consultas para este filtro."}
              </Typography>
            ) : (
              <>
                <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 6 }}>
                  {list.count} consulta(s){loading ? " · actualizando…" : ""} · los casos borde van arriba · en rojo, más de 8 h esperando
                </Typography>
                {list.requests.map((r) => (
                  <RequestCard key={r.id} r={r} canChecklist={!!me?.canChecklist} onOpen={(id) => setQuery({ consulta: id })} onChanged={() => setRefreshTick((t) => t + 1)} />
                ))}
                {list.hasMore && <Button onClick={() => loadList(page + 1)} disabled={loading}>Ver más</Button>}
              </>
            )}
          </>
        )}
        {view === "banco" && <KnowledgeTab master={master} onOpenRequest={(id) => setQuery({ consulta: id })} />}
        {view === "cobertura" && <CoverageTab master={master} onOpenRequest={(id) => setQuery({ consulta: id })} />}
        {view === "config" && master && <ConfigTab />}
      </Box>

      {openParam && (
        <RequestDetail
          requestId={openParam}
          canChecklist={!!me?.canChecklist}
          onClose={() => setQuery({ consulta: null })}
          onChanged={() => setRefreshTick((t) => t + 1)}
        />
      )}
    </div>
  );
};

export default SuperAdminAtencion;
